import { useState, useEffect, useCallback } from "react";
import { Bell, BellRing, Check, X, MessageCircle, Heart, AlertTriangle, Volume2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { SpeakButton } from "./SpeakButton";

// Convert VAPID key from base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default function NotificationCenter() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem("ea_token");
  const headers = { Authorization: `Bearer ${token}` };

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!user || !token) return;
    try {
      const res = await axios.get(`${API}/notifications`, { headers });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (err) {
      console.error("Error fetching notifications:", err);
    }
  }, [user, token]);

  // Check push subscription status
  const checkPushStatus = useCallback(async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      return;
    }
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setPushEnabled(!!subscription);
    } catch (err) {
      console.error("Error checking push status:", err);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    checkPushStatus();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications, checkPushStatus]);

  // Register service worker and subscribe to push
  const subscribeToPush = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      toast.error("Tu navegador no soporta notificaciones push");
      return;
    }

    setLoading(true);
    try {
      // Register service worker
      const registration = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;

      // Request permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error("Permiso de notificaciones denegado");
        setLoading(false);
        return;
      }

      // Get VAPID public key
      const vapidRes = await axios.get(`${API}/push/vapid-key`);
      const vapidPublicKey = vapidRes.data.publicKey;

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Send subscription to backend
      const subJson = subscription.toJSON();
      await axios.post(`${API}/push/subscribe`, {
        endpoint: subJson.endpoint,
        keys: subJson.keys
      }, { headers });

      setPushEnabled(true);
      toast.success("Notificaciones push activadas!");
    } catch (err) {
      console.error("Push subscription error:", err);
      toast.error("Error al activar notificaciones");
    }
    setLoading(false);
  };

  // Unsubscribe from push
  const unsubscribeFromPush = async () => {
    setLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
        await axios.delete(`${API}/push/unsubscribe`, {
          headers,
          data: { endpoint: subscription.endpoint }
        });
      }
      
      setPushEnabled(false);
      toast.success("Notificaciones push desactivadas");
    } catch (err) {
      console.error("Unsubscribe error:", err);
      toast.error("Error al desactivar notificaciones");
    }
    setLoading(false);
  };

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, { headers });
      setNotifications(prev => 
        prev.map(n => n.notification_id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Error marking as read:", err);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { headers });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error("Error marking all as read:", err);
    }
  };

  // Test push notification
  const testPush = async () => {
    try {
      await axios.post(`${API}/push/test`, {}, { headers });
      toast.success("Notificación de prueba enviada!");
    } catch (err) {
      toast.error("Error al enviar prueba");
    }
  };

  // Get icon based on notification type
  const getIcon = (type) => {
    switch (type) {
      case 'chat': return <MessageCircle className="w-4 h-4 text-[var(--blue-vibrant)]" />;
      case 'offer_accepted': return <Heart className="w-4 h-4 text-green-500" />;
      case 'offer_rejected': return <X className="w-4 h-4 text-red-400" />;
      case 'emergency': return <AlertTriangle className="w-4 h-4 text-orange-500" />;
      default: return <Bell className="w-4 h-4 text-[var(--purple)]" />;
    }
  };

  // Format time
  const formatTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Ahora';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  };

  if (!user) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          data-testid="notification-bell"
        >
          {unreadCount > 0 ? (
            <BellRing className="w-5 h-5 text-[var(--blue-vibrant)] animate-pulse" />
          ) : (
            <Bell className="w-5 h-5 text-[var(--navy)]" />
          )}
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-[var(--coral)] text-white text-xs rounded-full flex items-center justify-center font-bold">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto" data-testid="notification-dropdown">
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <span className="font-heading font-semibold text-sm text-[var(--navy)]">Notificaciones</span>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs text-[var(--blue-vibrant)] hover:underline"
                data-testid="mark-all-read"
              >
                Marcar leidas
              </button>
            )}
          </div>
        </div>

        {/* Push toggle */}
        <div className="px-3 py-2 border-b bg-[var(--cream-light)]">
          <div className="flex items-center justify-between">
            <span className="text-xs text-[var(--navy)]">
              Notificaciones push
            </span>
            <Button
              variant={pushEnabled ? "outline" : "default"}
              size="sm"
              className="h-7 text-xs"
              onClick={pushEnabled ? unsubscribeFromPush : subscribeToPush}
              disabled={loading}
              data-testid="push-toggle"
            >
              {loading ? (
                <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : pushEnabled ? (
                "Desactivar"
              ) : (
                "Activar"
              )}
            </Button>
          </div>
          {pushEnabled && (
            <button
              onClick={testPush}
              className="text-xs text-[var(--purple)] hover:underline mt-1"
              data-testid="test-push-btn"
            >
              Enviar prueba
            </button>
          )}
        </div>

        {/* Notification list */}
        {notifications.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm text-gray-500">
            No tienes notificaciones
          </div>
        ) : (
          notifications.slice(0, 10).map((notif) => (
            <DropdownMenuItem
              key={notif.notification_id}
              className={`px-3 py-3 cursor-pointer ${!notif.read ? 'bg-[var(--cream-light)]' : ''}`}
              onClick={() => {
                if (!notif.read) markAsRead(notif.notification_id);
                if (notif.link) window.location.href = notif.link;
              }}
              data-testid={`notification-item-${notif.notification_id}`}
            >
              <div className="flex gap-3 w-full">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-sm">
                  {getIcon(notif.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[var(--navy)] truncate">
                    {notif.title}
                  </p>
                  <p className="text-xs text-gray-600 line-clamp-2">
                    {notif.message}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-xs text-gray-400">
                      {formatTime(notif.created_at)}
                    </p>
                    <SpeakButton text={`${notif.title}. ${notif.message}`} />
                  </div>
                </div>
                {!notif.read && (
                  <div className="w-2 h-2 rounded-full bg-[var(--blue-vibrant)] flex-shrink-0 mt-2" />
                )}
              </div>
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
